/**
 * What goes with every prompt the IDE sends (docs/refactor/21-steering-local-e-compartilhado.md):
 *   - the project's pinned instructions (shared: every AI of the project);
 *   - this machine's own instructions (local: only here, only for whoever wrote them — kept in this
 *     app, never sent to Vephus);
 *   - the skills switched on here, by name and what they are for.
 * Claude Code gets it as an appended system prompt on every call (agents.rs); other agents, before the
 * prompt. The whole is capped so the person's request is not buried under it.
 */

import type { Permission } from './localAgent'

const LOCAL_KEY = 'devpulse.localInstructions'

/** The most that goes with a prompt: past this, the end is cut and the person is told. */
export const WITH_EVERY_PROMPT_MAX = 20_000

export function readLocalInstructions(): string {
  try {
    return localStorage.getItem(LOCAL_KEY) ?? ''
  } catch {
    return ''
  }
}

export function saveLocalInstructions(text: string): void {
  try {
    if (text.trim()) localStorage.setItem(LOCAL_KEY, text.slice(0, WITH_EVERY_PROMPT_MAX))
    else localStorage.removeItem(LOCAL_KEY)
  } catch {
    // No storage: nothing kept, nothing sent.
  }
}

/**
 * "Você está no Vephus" (docs/refactor/19-ia-sabe-onde-esta.md): where the agent is and what it can do,
 * in a few lines, before the instructions. The detail it asks through the MCP.
 */
export interface VephusContext {
  person: string | null
  project: string | null
  machine: string | null
  /** The person's other computers that are on and take commands now. */
  otherMachines: string[]
  /** This computer is the person's principal (docs/refactor/23); or the principal's name, when another one is. */
  principal?: boolean
  principalName?: string | null
  /** How many agents (tabs) the person has on this computer: they can each take a part of a task. */
  agentsHere?: number
  /** What the person let the agent do on this computer without asking (Conectar agente; docs/refactor/59). */
  permission?: Permission
}

/**
 * The permission in the agent's own words (docs/refactor/59): "computer" is the one that lets it leave the
 * project, so a request to open a program or write a document elsewhere is done, not declined; the other two
 * say where the person switches it on, so a request they cannot serve ends with the path, not a silent refusal.
 */
export function permissionText(permission: Permission): string {
  const where = 'em Configurações › Conectar agente › "O que o agente pode fazer sem perguntar"'
  switch (permission) {
    case 'computer':
      return 'Permissão da pessoa neste computador: o computador inteiro. Você pode abrir programas (o Word, o navegador, o que for preciso), criar e mudar arquivos e configurações fora da pasta do projeto e rodar comandos, sem pedir confirmação. Um pedido fora do projeto é para ser feito, não recusado; no fim, conte o que fez e o que mudou fora do projeto.'
    case 'all':
      return `Permissão da pessoa neste computador: tudo no projeto, inclusive rodar comandos, sem pedir confirmação. O trabalho é na pasta do projeto; para abrir programas ou mexer fora dela, a pessoa liga "O computador inteiro" ${where}. Diga isso quando um pedido precisar.`
    default:
      // Agent-neutral (review of 0.122.0): Claude Code may run tests, git and builds; OpenCode runs no command; Codex
      // runs commands only inside the project, with no internet.
      return `Permissão da pessoa neste computador: só editar arquivos do projeto, e comandos só os poucos que o seu próprio nível libera (testes, git e builds no Claude Code; nenhum no OpenCode; só dentro do projeto e sem internet no Codex); o resto é recusado. Quando um pedido precisar de mais, diga que isso se liga ${where}.`
  }
}

export function contextText(c: VephusContext): string {
  const lines = [
    '# Você está no Vephus',
    '',
    `Você trabalha${c.person ? ` para ${c.person}` : ''}${c.project ? ` no projeto ${c.project}` : ''}${c.machine ? `, no computador ${c.machine}` : ''}, pelo Vephus (o app que acompanha e coordena os agentes da pessoa).`,
    ...(c.principal
      ? ['Este computador é o principal da pessoa: tarefas com vários agentes mandadas de fora são orquestradas aqui.']
      : c.principalName
        ? [`O computador principal da pessoa é ${c.principalName}: tarefas com vários agentes mandadas de fora são orquestradas lá.`]
        : []),
    ...(c.agentsHere && c.agentsHere > 1
      ? [`Neste computador a pessoa tem ${c.agentsHere} agentes (Agente 1 a ${c.agentsHere}); numa tarefa em conjunto, cada um pode pegar uma parte (devpulse_task_delegate).`]
      : []),
    ...(c.permission ? [permissionText(c.permission)] : []),
    c.otherMachines.length
      ? `Outros computadores da pessoa estão ligados e podem fazer parte do trabalho: ${c.otherMachines.join(', ')} (mande com devpulse_queue_send e machine).`
      : 'Nenhum outro computador da pessoa está pegando comandos agora.',
    'Pelo MCP do Vephus (ferramentas devpulse_*) você pode: ver e mandar trabalho a outros computadores (devpulse_machines, devpulse_queue_send), ler e escrever a documentação do projeto (devpulse_docs_*), registrar e mover bugs (devpulse_bug*), ver sessões e relatórios (devpulse_sessions, devpulse_report*), trabalhar com outros agentes numa tarefa em conjunto (devpulse_task_*), consultar a memória das IAs — o que você e as outras IAs do projeto já pediram, fizeram e responderam nos últimos dias (devpulse_memory_search, devpulse_memory_read) —, ver a caixa de entrada da pessoa e marcar o que resolveu (devpulse_inbox, devpulse_inbox_mark) e dizer o que está fazendo (devpulse_start_work, devpulse_say).',
  ]
  return lines.join('\n')
}

export interface WithEveryPrompt {
  /** Where the agent is (contextText); first, when given. */
  context?: string
  /** The project's pinned instructions, already as one text (the server's). Empty when none. */
  project: string
  /** This machine's own. */
  local: string
  /** The skills on here: name and description. */
  skills: Array<{ name: string; description: string }>
}

/** The text that goes with every prompt, and whether it had to be cut to fit. */
export function withEveryPrompt(parts: WithEveryPrompt): { text: string; cut: boolean } {
  const sections: string[] = []
  if (parts.context?.trim()) sections.push(parts.context.trim())
  if (parts.project.trim()) sections.push(`# Instruções do projeto (valem para todas as IAs do projeto)\n\n${parts.project.trim()}`)
  if (parts.local.trim()) sections.push(`# Instruções desta máquina (só de quem está neste computador)\n\n${parts.local.trim()}`)
  if (parts.skills.length)
    sections.push(`# Skills ligadas neste computador\n\n${parts.skills.map((s) => `- ${s.name}${s.description ? `: ${s.description}` : ''}`).join('\n')}`)
  const text = sections.join('\n\n')
  return text.length > WITH_EVERY_PROMPT_MAX ? { text: text.slice(0, WITH_EVERY_PROMPT_MAX), cut: true } : { text, cut: false }
}
